import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pencil, X } from 'lucide-react';
import {
  updateDocumentoImpianto,
  type DocumentoImpianto,
  type DocumentoImpiantoTipo,
} from '../../api/documentiImpianto';

const inputCls =
  'w-full text-sm text-ink-dark border border-stone-border rounded px-2 py-1.5 focus:outline-none focus:border-sage focus:ring-1 focus:ring-sage/20 bg-white';
const selectCls = `${inputCls} cursor-pointer`;

interface Props {
  documento: DocumentoImpianto;
  onClose: () => void;
  onSaved: (doc: DocumentoImpianto) => void;
}

export default function EditDocumentoImpiantoModal({ documento, onClose, onSaved }: Props) {
  const { t } = useTranslation();
  const [nome, setNome] = useState(documento.nome);
  const [descrizione, setDescrizione] = useState(documento.descrizione ?? '');
  const [tipo, setTipo] = useState<DocumentoImpiantoTipo>(documento.tipo);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const canSubmit = nome.trim().length > 0 && !submitting;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const saved = await updateDocumentoImpianto(documento.id, {
        nome: nome.trim(),
        descrizione: descrizione.trim() || null,
        tipo,
      });
      onSaved(saved);
    } catch (err: unknown) {
      const resp = (err as { response?: { data?: { detail?: string } } })?.response;
      setError(resp?.data?.detail ?? t('documenti.edit_modal.errore_salvataggio'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="edit-modal">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-stone-mid">
          <h2 className="text-base font-semibold text-ink-dark flex items-center gap-2">
            <Pencil size={16} className="text-sage" />
            {t('documenti.edit_modal.titolo')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="text-ink-soft hover:text-ink-dark"
            data-testid="edit-modal-cancel"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="px-6 py-5 space-y-4">
            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('documenti.edit_modal.nome')}</label>
              <input
                type="text"
                maxLength={255}
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                className={inputCls}
                data-testid="edit-modal-nome"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('documenti.edit_modal.tipo')}</label>
              <select
                value={tipo}
                onChange={(e) => setTipo(e.target.value as DocumentoImpiantoTipo)}
                className={selectCls}
                data-testid="edit-modal-tipo"
              >
                <option value="normativa">{t('documenti.tipo.normativa')}</option>
                <option value="scheda_tecnica">{t('documenti.tipo.scheda_tecnica')}</option>
                <option value="autorizzazione">{t('documenti.tipo.autorizzazione')}</option>
                <option value="altro">{t('documenti.tipo.altro')}</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-medium text-ink-soft mb-1">{t('documenti.edit_modal.descrizione')}</label>
              <textarea
                value={descrizione}
                onChange={(e) => setDescrizione(e.target.value)}
                rows={3}
                className={inputCls}
                data-testid="edit-modal-descrizione"
              />
            </div>

            {error && (
              <div
                className="px-3 py-2 bg-accent-danger-bg border border-[#FECACA] rounded text-sm text-accent-danger"
                data-testid="edit-modal-error"
              >
                {error}
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-stone-mid bg-stone-bg">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium text-ink-mid border border-stone-border rounded-lg hover:bg-white transition-colors disabled:opacity-50"
            >
              {t('common.azioni.annulla')}
            </button>
            <button
              type="submit"
              disabled={!canSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-sage rounded-lg hover:bg-sage-dark transition-colors disabled:opacity-60"
              data-testid="edit-modal-submit"
            >
              {submitting ? t('documenti.edit_modal.salvataggio') : t('documenti.edit_modal.salva')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
